import React, { useCallback, type RefObject } from 'react';
import styled from 'styled-components';
import Pencil from '@splunk/react-icons/Pencil';
import Button from '@splunk/react-ui/Button';
import { variables } from '@splunk/themes';
import { useRestoreFocus } from '@splunk/dashboard-context';

// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
const revertPointerEvents: React.CSSProperties = { pointerEvents: 'initial' };

interface EditButtonExtraProps {
    labelledBy?: string;
}

export interface EditButtonProps {
    onEditClick?: React.MouseEventHandler;
    editButtonRef?: RefObject<HTMLButtonElement> | null;
}

const StyledButton = styled(Button)`
    width: ${variables.inputHeight};
`;

const pencilIcon = (
    <Pencil
        data-test="edit-icon"
        aria-hidden={false}
        style={revertPointerEvents}
    />
);

export const EditButton = ({
    onEditClick,
    labelledBy,
    editButtonRef,
}: EditButtonProps & EditButtonExtraProps) => {
    const setReturnPoint = useRestoreFocus(editButtonRef ?? null);

    const onEditClickWrapper = useCallback(
        (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
            onEditClick?.(event);
            setReturnPoint();
        },
        [onEditClick, setReturnPoint]
    );

    if (!onEditClick) {
        return null;
    }

    return (
        <StyledButton
            ref={editButtonRef}
            data-test="item-edit"
            appearance="subtle"
            icon={pencilIcon}
            onClick={onEditClickWrapper}
            aria-labelledby={labelledBy}
        />
    );
};
